import React, { type FC } from 'react';
import {
  ImageLoadingBehaviorOptions as LoadingBehaviorOptions,
  ImageResizeOptions as ResizeOptions,
  ThumbnailImage,
} from '@wix/wix-ui-tpa/cssVars';
import { useWidgetViewModel } from '../../../hooks/useWidgetViewModel';
import { classes, st } from './ServiceImage.st.css';
import { DataHooks } from './consts';
import { Image } from '@wix/ambassador-bookings-services-v2-service/types';
import { sdk as imageSDK } from '@wix/image-kit';
import { ImageResizeOptions, ImageShapeOptions } from '../../../../types';
import { useStyles } from '@wix/tpa-settings/react';
import stylesParams from '../../../../stylesParams';
import { getImageAspectRatio } from '../../../../controller/viewModel/serviceImageViewModel/serviceImageViewModel';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { getStringStyleParamsValue } from '../../../../styleParamsUtils';

export type ServiceImageProps = {
  image?: Image;
  imageTitle: string;
  width: number;
  height: number;
  onClick(): void;
  className?: string;
};

const mapImageResizeOptionsToResizeOptions = {
  [ImageResizeOptions.CROP]: ResizeOptions.cover,
  [ImageResizeOptions.FIT]: ResizeOptions.contain,
};

export const ServiceImage: FC<ServiceImageProps> = (props) => {
  const { image, onClick, className } = props;
  const { isSEO, serviceImageViewModel } = useWidgetViewModel();
  const { isMobile } = useEnvironment();
  const { imageResize, focalPoint } = serviceImageViewModel;
  const { height = 0, width = 9, url = '' } = image ?? {};
  const styles = useStyles();
  // We cannot pass image.url directly to ThumbnailImage src prop because the image url might be in an old format (e.g. prefixed by "media/"),
  // Which ThumbnailImage doesn't handle properly. The image SDK methods normalize the URL so it is valid for ThumbnailImage.
  const imageUrl =
    imageResize === ImageResizeOptions.CROP
      ? imageSDK.getScaleToFillImageURL(url, width, height, width, height, {
          focalPoint,
        })
      : imageSDK.getScaleToFitImageURL(url, width, height, width, height);
  const displayImage = styles.get(stylesParams._imageDisplay);

  return (
    <div data-hook={DataHooks.ROOT} className={st(classes.root, className)}>
      <div data-hook={DataHooks.WRAPPER} className={st(classes.wrapper)}>
        <div
          data-hook={DataHooks.CONTAINER}
          onClick={onClick}
          className={classes.clickable}
          role="presentation"
          tabIndex={-1}
        >
          {image && url && displayImage ? (
            <ThumbnailImage
              alt={image.altText || undefined}
              fluid
              width={isSEO ? image.width : 50}
              height={isSEO ? image.height : 50}
              focalPoint={focalPoint}
              loadingBehavior={LoadingBehaviorOptions.none}
              resize={mapImageResizeOptionsToResizeOptions[imageResize]}
              data-hook={DataHooks.IMAGE}
              className={classes.image}
              src={imageUrl}
              sourceHeight={image.height}
              sourceWidth={image.width}
              shouldUseLQIP
              isSEOBot={isSEO}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
